import { observer } from "mobx-react";
import { IAppliedFilterChipComponent } from "../BaseChipComponents/IAppliedFilterChipComponent";
import {
    chipAggregatedLabel,
    chipItemLabel,
    renderCollectionChips,
    useChipFiltersStore,
} from "../BaseChipComponents/chipUtils";

/**
 * Chips for the Primary Driver Theme filter. Unlike {@link TopicsChips}, these never carry a
 * "(Deprecated)" marker — this is the supported Theme filter.
 *
 * The chip label is a plain string and cannot carry a status tag, so the marker on the legacy
 * sibling is what tells the two Theme filters apart here. That marker is viewer-specific, but this
 * side needs no such gate: only a viewer entitled to Drivers sees these chips at all, and that is
 * the same entitlement that makes the sibling's marker visible.
 */
export const ThemesChips = observer(function (
    props: IAppliedFilterChipComponent,
) {
    const applicationFiltersStore = useChipFiltersStore(props);

    const themes = applicationFiltersStore.themes;
    const isExclusion = !applicationFiltersStore.containsThemes;
    const setThemesAndNormalizeMode = (nextThemes: typeof themes) => {
        applicationFiltersStore.setThemes(nextThemes);
        if (isExclusion && nextThemes.length === 0)
            applicationFiltersStore.setContainsThemes(true);
    };

    return renderCollectionChips({
        items: themes,
        disabled: props.disabled,
        isApplicable: props.isApplicable,
        getKey: (theme) => theme.id,
        getLabel: (theme) =>
            chipItemLabel("Theme", theme.themeLabel, isExclusion),
        getTooltipLabel: (theme) => theme.themeLabel,
        getAggregatedLabel: (count) =>
            chipAggregatedLabel(count, "Themes", isExclusion),
        onRemoveAll: () => setThemesAndNormalizeMode([]),
        onRemoveItem: (theme) =>
            setThemesAndNormalizeMode(
                applicationFiltersStore.themes.filter(
                    (t) => t.id !== theme.id,
                ),
            ),
    });
});
